<template>
  <ComboboxRoot
    v-model="selectedOption"
    :disabled="isDisabled"
    ignore-filter
    class="contents"
  >
    <ComboboxAnchor
      :class="[
        `design-system-input relative flex w-full items-stretch overflow-hidden rounded-lg border transition`,
        `outline-2 -outline-offset-1 focus-within:outline`,
        isDisabled && `opacity-50`,
        fieldContext?.validationStatus.value === 'error'
          ? `border-red-300 outline-red-500 dark:border-red-800`
          : `outline-blue-500`,
      ]"
    >
      <RadixComboboxInput
        :id="fieldContext?.inputId"
        ref="input"
        v-model="query"
        :display-value="renderDisplayValue"
        :placeholder="placeholder"
        :aria-invalid="fieldContext?.validationStatus.value === 'error'"
        :aria-describedby="ariaDescribedBy"
        class="min-w-0 grow border-0 bg-transparent px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:ring-0"
      />

      <div v-if="isPending" class="-my-1 -mr-2.5 p-2.5">
        <ArrowPathIcon
          class="m-0.5 size-4 animate-spin text-muted-foreground"
        />
      </div>

      <ComboboxTrigger
        class="grid size-8 shrink-0 place-items-center data-[state=open]:rotate-180"
      >
        <ChevronDownIcon class="size-4 text-muted-foreground" />
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxPortal>
      <Transition
        enter-active-class="transition-opacity duration-100 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ComboboxContent
          position="popper"
          :collision-padding="10"
          class="z-popover flex w-full flex-col rounded-md border-menu-border bg-menu text-menu-foreground shadow-lg dark:border"
          :class="$style.combobox_content"
        >
          <ComboboxViewport class="py-2">
            <slot v-if="filteredOptions.length === 0" name="empty-state">
              <div class="flex h-20 flex-col items-center justify-center px-3">
                <p class="text-sm text-menu-muted-foreground">No items</p>
              </div>
            </slot>

            <template
              v-for="(children, groupName, idx) in groupedOptions"
              :key="groupName"
            >
              <ComboboxSeparator
                v-if="idx !== 0"
                class="my-2 border-t border-menu-border"
              />

              <ComboboxGroup>
                <ComboboxLabel
                  v-if="groupName"
                  class="mx-5 my-1 shrink-0 select-none border-t-border text-[0.8125rem] leading-5 text-menu-muted-foreground"
                >
                  {{ groupName }}
                </ComboboxLabel>

                <ComboboxItem
                  v-for="option in children"
                  :key="option.id"
                  :value="option"
                  :disabled="option.disabled"
                  class="group relative mx-2 flex shrink-0 cursor-pointer select-none scroll-m-2 gap-3 self-stretch rounded-md border-none px-3 py-1 text-left text-sm outline-none transition-all duration-75 data-[highlighted]:bg-menu-muted"
                >
                  <span
                    class="min-w-0 grow break-words transition-opacity group-data-[disabled]:opacity-50"
                  >
                    {{ option.label }}
                  </span>

                  <CheckIcon
                    v-if="option.id !== '' || !option.disabled"
                    class="my-0.5 size-4 shrink-0 text-blue-400 opacity-0 transition-opacity duration-75 group-data-[state=checked]:opacity-100"
                  />
                </ComboboxItem>
              </ComboboxGroup>
            </template>
          </ComboboxViewport>
        </ComboboxContent>
      </Transition>
    </ComboboxPortal>
  </ComboboxRoot>
</template>

<script setup lang="ts">
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput as RadixComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxViewport,
} from "reka-ui";
import { computed, toRef, useTemplateRef } from "vue";

import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import CheckIcon from "@/icons/heroicons/check-micro.svg";
import ChevronDownIcon from "@/icons/heroicons/chevron-down-micro.svg";
import { useStablePending } from "@/lib/composables/stable-pending";

import { injectFieldContext } from "./context";

export interface ComboboxOption {
  id: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

const modelValue = defineModel<string>({ default: "" });
const query = defineModel<string>("query", { default: "" });

const props = defineProps<{
  options: readonly ComboboxOption[];
  placeholder?: string;
  pending?: boolean;
  disabled?: boolean;
  unfiltered?: boolean;
}>();

const input = useTemplateRef("input");

const fieldContext = injectFieldContext(null);

const isPending = useStablePending(toRef(props, "pending"));
const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});

const selectedOption = computed({
  get() {
    const value = modelValue.value;
    const option = props.options.find((opt) => opt.id === value);

    return option;
  },
  set(next) {
    modelValue.value = next ? next.id : "";
  },
});

const filteredOptions = computed(() => {
  if (props.unfiltered) {
    return props.options;
  }

  const q = query.value.trim().toLowerCase();

  return props.options.filter((option) => {
    return option.label.toLowerCase().includes(q);
  });
});

const groupedOptions = computed(() => {
  const ungroupedOptions: ComboboxOption[] = [];
  const groups: Record<string, ComboboxOption[]> = {};

  for (const option of filteredOptions.value) {
    const groupName = option.group ?? "";

    if (groupName === "") {
      ungroupedOptions.push(option);
      continue;
    }

    const group = (groups[groupName] ??= []);
    group.push(option);
  }

  // Always sort ungrouped options first
  return ungroupedOptions.length ? { "": ungroupedOptions, ...groups } : groups;
});

const renderDisplayValue = (item: unknown): string => {
  if (!item) return "";
  return (item as ComboboxOption).label;
};

defineExpose({
  input: computed(() => {
    return input.value?.$el as HTMLInputElement | null | undefined;
  }),
});
</script>

<style module>
.combobox_content {
  max-height: var(--reka-combobox-content-available-height);
  width: var(--reka-combobox-trigger-width);
}
</style>
